import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { environment } from './../../../../environments/environment';
import { API } from '../../API';

export interface Club {
  client_id: number;
  club_id: number;
  federation_id: number;
  club_code: string;
  club_name: string;
  Cle_CLUB: string;
  Statut_CLUB: 'ACTI' | 'INAC' | 'DESA';
  club_status?: 'ACTI' | 'INAC' | 'DESA';
  logo_club?: string;
}

export interface Federation {
  federation_id: number;
  federation_acornyme: string;
  federation_name: string;
  logo_url?: string;
}

// TASK-1.07: token is no longer in JSON response — it is set as an HttpOnly cookie
export interface AuthResponse {
  success: boolean;
  user: {
    id: number;
    username: string;
    roles: string[];
    user_object_type: string;
    user_object_id?: number;
    permissions?: Record<string, string[]>;
  };
  club?: Club;
  federation?: Federation;
  // Platform account (SIQ-124): no federation until one is chosen
  platform?: boolean;
  needs_federation?: boolean;
  lost_federation?: boolean;
}

export interface FederationChoice {
  federation_id: number;
  federation_acornyme: string;
  federation_name: string;
}

export interface User {
  id: number;
  username: string;
  roles: string[];
  user_object_type: string;
  user_object_id?: number;
  permissions?: Record<string, string[]>;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly baseUrl = environment.apiUrl;
  private userDataSignal = signal<User | null>(null);
  private clubDataSignal = signal<Club | null>(null);
  private federationDataSignal = signal<Federation | null>(null);
  // Platform account: true until a federation is chosen (backend answers 403 FEDERATION_REQUIRED before that)
  private needsFederationSignal = signal(false);
  /** Read-only view of the working federation (null = none chosen) — the sidebar and layout react to it. */
  readonly currentFederation = this.federationDataSignal.asReadonly();
  /** Message shown by the layout when the working federation became invalid (null = nothing to say). */
  readonly lostFederationNotice = signal<string | null>(null);
  private platformSignal = signal(false);
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private httpClient: HttpClient) {}

  /**
   * Restore session from HttpOnly cookie on app startup.
   * Called by APP_INITIALIZER — if cookie is valid, backend returns user data.
   */
  restoreSession(): Observable<AuthResponse | null> {
    return this.httpClient.get<AuthResponse>(`${this.baseUrl}${API.AUTH.ME}`, { withCredentials: true }).pipe(
      tap(response => { if (response.success) this.applyMe(response); }),
      catchError(() => {
        // Cookie expired or invalid — user stays logged out
        return of(null);
      })
    );
  }

  /**
   * Login of the Digi4 platform account: username + password only (no federation code).
   * The federation to work on is chosen afterwards (see selectFederation).
   */
  login(username: string, password: string): Observable<AuthResponse> {
    const endpoint = API.AUTH.LOGIN;
    return this.httpClient.post<AuthResponse>(`${this.baseUrl}${endpoint}`, {
      username,
      password,
    }, { withCredentials: true }).pipe(
      switchMap(loginResponse => {
        if (!loginResponse.success) return of(loginResponse);
        // Login succeeded — cookie is set. Now fetch permissions from /auth/me
        return this.httpClient.get<AuthResponse>(`${this.baseUrl}${API.AUTH.ME}`, { withCredentials: true }).pipe(
          tap(meResponse => { if (meResponse.success) this.applyMe(meResponse); }),
          // If /me fails, still use login response data (without permissions)
          catchError(() => {
            this.setUser(loginResponse.user);
            if (loginResponse.club) this.setClub(loginResponse.club);
            if (loginResponse.federation) this.setFederation(loginResponse.federation);
            this.platformSignal.set(true);
            this.needsFederationSignal.set(!!loginResponse.needs_federation);
            this.currentUserSubject.next(loginResponse.user);
            return of(loginResponse);
          })
        );
      })
    );
  }

  /** Federations the platform account can work on. */
  listFederations(): Observable<{ success: boolean; current_federation_id: number | null; data: FederationChoice[] }> {
    return this.httpClient.get<{ success: boolean; current_federation_id: number | null; data: FederationChoice[] }>(
      `${this.baseUrl}${API.AUTH.FEDERATIONS}`, { withCredentials: true });
  }

  /** The session now works on this federation: the backend re-issues the cookie, permissions are those of that federation. */
  selectFederation(targetFederation: number): Observable<{ success: boolean; federation: Federation | null; permissions: Record<string, string[]> }> {
    return this.httpClient.post<{ success: boolean; federation: Federation | null; permissions: Record<string, string[]> }>(
      `${this.baseUrl}${API.AUTH.SELECT_FEDERATION}`, { target_federation: targetFederation }, { withCredentials: true }).pipe(
      tap(r => {
        if (!r.success) return;
        const user = this.getUser();
        if (user) this.setUser({ ...user, permissions: r.permissions });
        if (r.federation) this.setFederation(r.federation);
        this.needsFederationSignal.set(false);
        const u = this.getUser();
        if (u) this.currentUserSubject.next(u);
      })
    );
  }

  /** Back to the Plateforme zone: the backend re-issues the cookie with no federation, permissions = platform rights only. */
  leaveFederation(): Observable<{ success: boolean; permissions: Record<string, string[]> }> {
    return this.httpClient.post<{ success: boolean; permissions: Record<string, string[]> }>(
      `${this.baseUrl}${API.AUTH.LEAVE_FEDERATION}`, {}, { withCredentials: true }).pipe(
      tap(r => {
        if (!r.success) return;
        const user = this.getUser();
        if (user) this.setUser({ ...user, permissions: r.permissions });
        this.federationDataSignal.set(null);
        this.needsFederationSignal.set(true);
        const u = this.getUser();
        if (u) this.currentUserSubject.next(u);
      })
    );
  }

  /** Where a session without federation lands: the platform zone, or the choose page for accounts without platform rights. */
  homeWithoutFederation(): string {
    return this.hasPlatformRights() ? '/plateforme/federations' : '/federation/auth/select-federation';
  }

  /** Platform zone right (manage every federation), independent of the chosen federation. */
  hasPlatformRights(): boolean {
    return this.getUser()?.permissions?.['main:federations']?.includes('read') ?? false;
  }

  /** A platform page changed / deleted this federation: if it is the working one, re-read the session (/auth/me says lost_federation when it became invalid). */
  refreshIfCurrent(federationId: number): void {
    if (this.currentFederation()?.federation_id === federationId) this.restoreSession().subscribe();
  }

  clearLostFederationNotice(): void {
    this.lostFederationNotice.set(null);
  }

  /** The working federation was deleted / deactivated / lost its backoffice module: back to "no federation". */
  dropFederation(message = "La fédération de travail n'est plus disponible (supprimée, désactivée ou module backoffice coupé). Choisissez-en une autre."): Observable<unknown> {
    this.lostFederationNotice.set(message);
    this.federationDataSignal.set(null);
    this.needsFederationSignal.set(true);
    return this.leaveFederation().pipe(catchError(() => of(null)));
  }

  /** Platform account with no federation chosen yet. */
  needsFederation(): boolean {
    return this.needsFederationSignal();
  }

  isPlatform(): boolean {
    return this.platformSignal();
  }

  logout(): Observable<any> {
    const endpoint = API.AUTH.LOGOUT;
    // TASK-1.07: Cookie is sent automatically via withCredentials — no token in body needed
    // Backend uses req.user (from cookie auth) to identify the user
    return this.httpClient.post(`${this.baseUrl}${endpoint}`, {}, { withCredentials: true }).pipe(
      tap(() => this.clearAuth()),
      catchError((error: any) => {
        console.error('Logout error:', error);
        // Clear local state even if server logout fails
        this.clearAuth();
        return new Observable(observer => observer.complete());
      })
    );
  }

  // TASK-1.11: Transparent token refresh — cookie is sent automatically
  refreshToken(): Observable<any> {
    return this.httpClient.post(`${this.baseUrl}/auth/refresh`, {}, { withCredentials: true });
  }

  // Called by AuthInterceptor directly when refresh fails — avoids HTTP loop
  clearLocalAuth(): void {
    this.userDataSignal.set(null);
    this.clubDataSignal.set(null);
    this.federationDataSignal.set(null);
    this.needsFederationSignal.set(false);
    this.platformSignal.set(false);
    this.currentUserSubject.next(null);
  }

  private applyMe(response: AuthResponse): void {
    this.setUser(response.user);
    if (response.club) this.setClub(response.club);
    this.federationDataSignal.set(response.federation ?? null);
    this.platformSignal.set(!!response.platform);
    this.needsFederationSignal.set(!!response.needs_federation);
    this.currentUserSubject.next(response.user);
    // The token still names the lost federation: reset it to "no federation" (backend leave-federation) and tell the user.
    if (response.lost_federation) this.dropFederation().subscribe();
  }

  private clearAuth(): void {
    this.clearLocalAuth();
  }

  // TASK-1.09: Token is in HttpOnly cookie — JS cannot read it.
  // Club status is read from stored club_data instead of JWT payload.
  getClubStatusFromToken(): 'ACTI' | 'INAC' | 'DESA' | null {
    const club = this.getClub();
    if (!club) return null;
    const status = club.club_status || club.Statut_CLUB || null;
    if (status === 'ACTI' || status === 'INAC' || status === 'DESA') return status;
    return null;
  }

  getAccessLevel(): 'full' | 'readonly' | 'blocked' {
    if (!this.isAuthenticated()) return 'readonly';
    if (!this.isClub()) return 'full';

    const status = this.getClubStatusFromToken();
    if (status === 'ACTI') return 'full';
    if (status === 'INAC') return 'readonly';
    if (status === 'DESA') return 'blocked';
    return 'readonly';
  }

  setUser(user: User): void {
    this.userDataSignal.set(user);
  }

  getUser(): User | null {
    return this.userDataSignal();
  }

  // TASK-1.09: isAuthenticated checks user_data presence (not token)
  isAuthenticated(): boolean {
    return this.currentUserSubject.getValue() !== null;
  }

  isClub(): boolean {
    const user = this.getUser();
    return user?.user_object_type === 'CLUB';
  }

  isFederation(): boolean {
    const user = this.getUser();
    return user?.user_object_type === 'FEDE';
  }

  setClub(club: Club): void {
    this.clubDataSignal.set(club);
  }

  getClub(): Club | null {
    return this.clubDataSignal();
  }

  setFederation(federation: Federation): void {
    this.federationDataSignal.set(federation);
  }

  getFederation(): Federation | null {
    return this.federationDataSignal();
  }

  getClubId(): number | null {
    const club = this.getClub();
    return club ? club.club_id : null;
  }
}
